// Jana PDF resit 80mm (thermal) dengan jsPDF — teks + QR verifikasi.
// Dipakai selepas pengesahan bukti & rakaman kaunter: PDF di-upload ke Drive via drive-proxy.
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { formatRM } from './format';

const W = 80; // mm
const ML = 4; // margin kiri
const MR = 76; // x kanan

function dash(doc, y) {
  doc.setDrawColor(120);
  doc.setLineDashPattern([1.2, 1.2], 0);
  doc.line(ML, y, MR, y);
  doc.setLineDashPattern([], 0);
  return y + 3.5;
}

function row(doc, y, left, right, bold = false) {
  doc.setFont('courier', bold ? 'bold' : 'normal');
  doc.setFontSize(9);
  doc.text(String(left), ML, y);
  doc.text(String(right), MR, y, { align: 'right' });
  return y + 4.5;
}

function center(doc, y, text, size = 9, bold = false) {
  doc.setFont('courier', bold ? 'bold' : 'normal');
  doc.setFontSize(size);
  const lines = doc.splitTextToSize(String(text), W - ML * 2);
  doc.text(lines, W / 2, y, { align: 'center' });
  return y + lines.length * 4.2;
}

/**
 * @param {object} resit { no_resit, tanggal, studentNama, kelas, tarifNama, diskonNota, amount, kaedah, penyemak, baki }
 * @param {object} school { nama, alamat, telefon }
 * @param {object} opts { baseUrl } — untuk QR verifikasi (default: window.location.origin)
 * @returns {Promise<Blob>} PDF blob
 */
export async function resitPdfBlob({ resit, school, baseUrl }) {
  // Kira anggaran tinggi dahulu supaya halaman pas saiz kandungan (+ruang QR)
  const estRows = 16 + (resit.diskonNota ? 1 : 0) + (resit.baki > 0 ? 1 : 0);
  const H = Math.min(240, 78 + estRows * 4.5);

  const doc = new jsPDF({ unit: 'mm', format: [W, H], compress: true });
  doc.setTextColor(15, 23, 42);
  let y = 8;

  y = center(doc, y, school?.nama || 'MTSRpay', 11, true);
  if (school?.alamat) y = center(doc, y, school.alamat, 8);
  if (school?.telefon) y = center(doc, y, `Tel: ${school.telefon}`, 8);
  y += 1;
  y = dash(doc, y);

  y = row(doc, y, 'No. Resit', resit.no_resit, true);
  const tarikh = new Date(resit.tanggal).toLocaleString('ms-MY', { dateStyle: 'medium', timeStyle: 'short' });
  y = row(doc, y, 'Tarikh', tarikh);
  y = row(doc, y, 'Pelajar', (resit.studentNama || '').slice(0, 26));
  y = row(doc, y, 'Kelas', (resit.kelas || '-').slice(0, 26));
  y = dash(doc, y);

  y = row(doc, y, (resit.tarifNama || 'Yuran').slice(0, 24), formatRM(resit.amount));
  if (resit.diskonNota) y = row(doc, y, `Diskaun: ${resit.diskonNota}`.slice(0, 30), '');
  y = dash(doc, y);

  doc.setFont('courier', 'bold');
  doc.setFontSize(12);
  doc.text('JUMLAH', ML, y);
  doc.text(formatRM(resit.amount), MR, y, { align: 'right' });
  y += 6;

  y = row(doc, y, 'Kaedah', labelKaedah(resit.kaedah));
  if (resit.penyemak) y = row(doc, y, 'Disemak', (resit.penyemak || '').slice(0, 26));
  if (resit.baki > 0) y = row(doc, y, 'Baki', formatRM(resit.baki));
  y = dash(doc, y);

  y = center(doc, y, 'Terima kasih. Simpan resit ini sebagai bukti pembayaran.', 8);
  y += 2;

  // QR verifikasi resit — imbas untuk semak kesahihan di /verifikasi/:no_resit
  try {
    const origin = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
    const verifyUrl = `${origin}/verifikasi/${encodeURIComponent(resit.no_resit)}`;
    const qr = await QRCode.toDataURL(verifyUrl, { width: 200, margin: 1 });
    const qrSize = 26;
    doc.addImage(qr, 'PNG', (W - qrSize) / 2, y, qrSize, qrSize);
    y += qrSize + 2;
    y = center(doc, y, 'Imbas QR untuk verifikasi kesahihan resit', 7);
    y += 2;
  } catch {
    // QR gagal — resit tetap sah tanpa QR
  }

  center(doc, y, '*** Resit bercetak komputer — tandatangan tidak diperlukan ***', 7);

  return doc.output('blob');
}

function labelKaedah(k) {
  return { tunai: 'Tunai', transfer: 'Pindahan Bank', qr_manual: 'QR Manual' }[k] || k || '-';
}

export async function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
